import React, { useEffect, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { KitHistoryEntry } from "../../models/ArticleDTO";
import { requestErrorText } from "./kitUi";

export interface KitHistoryTimelineProps {
	campusId: number;
	/** Bitácora of one kit… */
	kitId?: number;
	/** …or of every kit of a student (newest first). */
	studentId?: number;
	/** Pre-loaded entries (skips the request). */
	entries?: KitHistoryEntry[];
	compact?: boolean;
	/** Permission section of the screen: "kits" (default: Control de Entregas, perfil del alumno) or "kitSales" (Control de Ventas). */
	section?: "kits" | "kitSales";
}

const TYPE_COLORS: Record<string, string> = {
	charged: "var(--wa-apagado)",
	advisors: "var(--wa-apagado)",
	payment: "var(--wa-verde)",
	payment_cancelled: "var(--wa-oro)",
	cancelled: "var(--wa-oro)",
	reactivated: "var(--wa-acento)",
	delivery_partial: "var(--wa-oro)",
	delivery_total: "var(--wa-verde)",
	delivery_status: "var(--wa-oro)",
	note: "var(--wa-apagado)"
};

/**
 * Read-only "bitácora" of a kit (kits overhaul, Sep 2026): who did what, when, with which note.
 * Used inside the student's financial profile and the Kits module. Never edits anything.
 */
const KitHistoryTimeline: React.FC<KitHistoryTimelineProps> = ({ campusId, kitId, studentId, entries, compact, section }) => {
	const [rows, setRows] = useState<KitHistoryEntry[]>(entries || []);
	const [loading, setLoading] = useState<boolean>(!entries);
	const [error, setError] = useState<string | null>(null);
	const http = HttpManager.getInstance();

	useEffect(() => {
		if (entries) { setRows(entries); setLoading(false); return; }
		let alive = true;
		setLoading(true);
		setError(null);
		const request = kitId
			? http.kitsService.getHistory(campusId, kitId, section || "kits")
			: http.kitsService.getStudentHistory(campusId, studentId as number, section || "kits");
		request
			.then((response) => { if (alive) setRows(response.data || []); })
			.catch((requestError) => { if (alive) setError(requestErrorText(requestError, "No se pudo cargar el historial.")); })
			.finally(() => { if (alive) setLoading(false); });
		return () => { alive = false; };
	}, [campusId, kitId, studentId, entries, section]);

	if (loading) return <p style={{ color: "var(--wa-apagado)", margin: 0 }}>Cargando historial…</p>;
	if (error) return <p style={{ color: "var(--wa-oro)", margin: 0 }}>{error}</p>;
	if (rows.length < 1) return <p style={{ color: "var(--wa-apagado)", margin: 0 }}>Sin movimientos registrados.</p>;

	return <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
		{rows.map((row) => <li key={row.id} style={{ display: "flex", gap: "10px", padding: compact ? "6px 0" : "10px 0", borderBottom: "1px solid var(--wa-filo)" }}>
			<span style={{ flex: "0 0 10px", marginTop: "6px", width: "10px", height: "10px", borderRadius: "50%", background: TYPE_COLORS[row.type] || "var(--wa-apagado)" }} />
			<div style={{ flex: 1, minWidth: 0 }}>
				<div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", alignItems: "baseline" }}>
					<b style={{ color: TYPE_COLORS[row.type] || "var(--wa-tinta)" }}>{row.typeLabel}</b>
					<span style={{ color: "var(--wa-apagado)", fontSize: "12px" }}>{row.createdAt}{row.author ? " · " + row.author : ""}</span>
					{row.kitTitle ? <span style={{ color: "var(--wa-apagado)", fontSize: "12px" }}>{row.kitTitle}{row.kitDescription ? " · " + row.kitDescription : ""}</span> : null}
				</div>
				<div style={{ fontSize: compact ? "13px" : "14px", wordBreak: "break-word" }}>{row.summary}</div>
				{row.note ? <div style={{ fontSize: "13px", color: "var(--wa-tinta)", background: "var(--wa-papel-2)", borderLeft: "3px solid var(--wa-filo-2)", padding: "4px 8px", marginTop: "4px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{row.note}</div> : null}
			</div>
		</li>)}
	</ul>;
};

export default KitHistoryTimeline;
